"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const fp = require("fp-ts");
const monocle = require("monocle-ts");
const _ActionTypes = require("./actionTypes");
const utils_1 = require("./utils");
function default_1(namespace) {
    const ActionTypes = _ActionTypes.default(namespace);
    const initialState = {
        status: 'STANDBY',
        groups: [],
        selectedGroups: [],
        selectedUsers: [],
        isCollapsed: [],
        searchString: '',
        sortingAndPagination: {
            offset: 0,
            limit: 25,
            sortBy: 'user',
            order: 'asc'
        },
        filters: {}
    };
    const statusLens = monocle.Lens.fromProp('status');
    const groupsLens = monocle.Lens.fromProp('groups');
    const selectedGroupsLens = monocle.Lens.fromProp('selectedGroups');
    const selectedUsersLens = monocle.Lens.fromProp('selectedUsers');
    const filtersLens = monocle.Lens.fromProp('filters');
    const isCollapsedLens = monocle.Lens.fromProp('isCollapsed');
    const searchStringLens = monocle.Lens.fromProp('searchString');
    const sortingOrderLens = monocle.Lens.fromPath(['sortingAndPagination', 'order']);
    const sortingSelectorLens = monocle.Lens.fromPath(['sortingAndPagination', 'sortBy']);
    const methods = {};
    // Fetch
    methods[ActionTypes.FETCH_REQUEST] = (state, action) => statusLens.set('FETCHING')(state);
    methods[ActionTypes.FETCH_SUCCESS] = (state, action) => {
        // purpose of this hokale below is just to make it structurally
        const memberSortBy = state.sortingAndPagination.sortBy === 'user'
            ? 'firstName'
            : state.sortingAndPagination.sortBy;
        return fp.function.compose((newState) => {
            if (state.groups.length === 0) {
                return isCollapsedLens.set(action.payload.map((x) => x.group.id))(newState);
            }
            return newState;
        }, statusLens.set('FETCHED'), groupsLens.set((0, utils_1.handleGroups)(action.payload, memberSortBy, state.sortingAndPagination.order)))(state);
    };
    methods[ActionTypes.FETCH_FAILED] = (state, action) => statusLens.set('FAILED')(state);
    methods[ActionTypes.CLEAR] = (state, action) => (Object.assign(Object.assign({}, state), initialState));
    methods[ActionTypes.COLLAPSE_USER_ROW] = (state, action) => {
        if (action.payload.isCollapsed) {
            return isCollapsedLens.modify((x) => action.payload.id.reduce((acc, item) => {
                if (!acc.includes(item)) {
                    acc.push(item);
                }
                return acc;
            }, [...x]))(state);
        }
        return isCollapsedLens.modify((list) => list.filter((x) => !action.payload.id.includes(x)))(state);
    };
    methods[ActionTypes.SET_GROUPS_SELECTED] = (state, action) => {
        const groups = [...state.selectedGroups];
        const users = [...state.selectedUsers];
        if (action.payload.selected) {
            for (const group of action.payload.items) {
                if (groups.findIndex((x) => x.id === group.id) === -1) {
                    groups.push(group);
                    const g = state.groups.find((x) => x.category.id === group.categoryId || x.category.id === group.id);
                    if (g.category.id === group.id) {
                        // Add category members
                        for (const u of g.category.members) {
                            if (users.findIndex((x) => x.user.id === u.id && x.group.id === g.category.id) === -1) {
                                users.push({ user: u, group: g.category });
                            }
                        }
                        // Add profile members
                        for (const sg of g.groups) {
                            if (groups.findIndex((x) => x.id === sg.id) === -1) {
                                groups.push(sg);
                            }
                            for (const u of sg.members) {
                                if (users.findIndex((x) => x.user.id === u.id && x.group.id === sg.id) === -1) {
                                    users.push({ user: u, group: sg });
                                }
                            }
                        }
                    }
                    else {
                        // Add profile members
                        const sg = g.groups.find((x) => x.id === group.id);
                        for (const u of sg.members) {
                            if (users.findIndex((x) => x.user.id === u.id && x.group.id === sg.id) === -1) {
                                users.push({ user: u, group: sg });
                            }
                        }
                    }
                }
            }
        }
        else {
            for (const group of action.payload.items) {
                const g = state.groups.find((x) => x.category.id === group.categoryId || x.category.id === group.id);
                if (g.category.id === group.id) {
                    // remove category
                    const index = groups.findIndex((x) => x.id === g.category.id);
                    index > -1 && groups.splice(index, 1);
                    // remove category members
                    for (const u of g.category.members) {
                        const index = users.findIndex((x) => x.user.id === u.id && x.group.id === g.category.id);
                        index > -1 && users.splice(index, 1);
                    }
                    for (const sg of g.groups) {
                        // remove sub groups
                        const index = groups.findIndex((x) => x.id === sg.id);
                        index > -1 && groups.splice(index, 1);
                        // remove sub group members
                        for (const u of sg.members) {
                            const index = users.findIndex((x) => x.user.id === u.id && x.group.id === sg.id);
                            index > -1 && users.splice(index, 1);
                        }
                    }
                }
                else {
                    const sg = g.groups.find((x) => x.id === group.id);
                    const index1 = groups.findIndex((x) => x.id === sg.id);
                    index1 > -1 && groups.splice(index1, 1);
                    const index2 = groups.findIndex((x) => x.id === sg.categoryId);
                    index2 > -1 && groups.splice(index2, 1);
                    for (const u of sg.members) {
                        const index = users.findIndex((x) => x.group.id === sg.id && x.user.id === u.id);
                        index > -1 && users.splice(index, 1);
                    }
                }
            }
        }
        return fp.function.compose(selectedGroupsLens.set(groups), selectedUsersLens.set(users))(state);
    };
    methods[ActionTypes.SET_USERS_SELECTED] = (state, action) => {
        const list = [...state.selectedUsers];
        if (action.payload.selected) {
            for (const item of action.payload.items) {
                if (list.findIndex((x) => x.group.id === item.group.id && x.user.id === item.user.id) === -1) {
                    list.push(item);
                }
            }
        }
        else {
            for (const item of action.payload.items) {
                const index = list.findIndex((x) => x.group.id === item.group.id && x.user.id === item.user.id);
                index > -1 && list.splice(index, 1);
            }
        }
        return selectedUsersLens.set(list)(state);
    };
    methods[ActionTypes.SET_SORTING_SELECTOR] = (state, action) => {
        const order = state.sortingAndPagination.order === 'asc' ? 'desc' : 'asc';
        const sortBy = state.sortingAndPagination.sortBy === action.sortBy
            ? action.sortBy
            : action.sortBy;
        // sort categories and user profiles to ascending alphabetical order
        const memberSortBy = sortBy === 'user' ? 'firstName' : sortBy;
        const sorted = state.groups.map((item) => {
            if (item.category.members) {
                item.category.members = (0, utils_1.sortElements)(item.category.members, memberSortBy, order);
            }
            item.groups = item.groups.map((group) => {
                if (group.members) {
                    group.members = (0, utils_1.sortElements)(group.members, memberSortBy, order);
                }
                return group;
            });
            return item;
        });
        return fp.function.compose(groupsLens.set(sorted), sortingOrderLens.set(order), sortingSelectorLens.set(action.sortBy))(state);
    };
    methods[ActionTypes.SET_COLUMN_FILTER] = (state, action) => filtersLens.modify((x) => {
        const clone = Object.assign({}, x);
        clone[action.payload.filter] = action.payload.value;
        return clone;
    })(state);
    methods[ActionTypes.SEARCH_GROUPS] = (state, action) => searchStringLens.set(action.searchString)(state);
    const reducer = (state, action) => {
        if (methods[action.type]) {
            return methods[action.type](state, action);
        }
        return state;
    };
    return {
        reducer,
        initialState,
        methods,
        ActionTypes,
        selectedGroupsLens,
        selectedUsersLens
    };
}
exports.default = default_1;
//# sourceMappingURL=reducers.js.map